import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { DataTableCard } from '../../components/DataTableCard'
import { PageScrollLayout } from '../../components/PageScrollLayout'
import { nextSortDirection, SortableTableHeader, type SortDirection } from '../../components/SortableTableHeader'
import { Api, ApiError, type PsRatingAuditRow } from '../../services/api'
import { fmtRating, formatDateTime, signed } from '../../utils/psRatingFormat'

type AuditSortKey = 'changed_at' | 'team' | 'old_rating' | 'new_rating' | 'delta' | 'changed_by' | 'reason'

export function PreSeasonRatingsAuditPage() {
  const [sortKey, setSortKey] = useState<AuditSortKey>('changed_at')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')

  const auditQuery = useQuery({
    queryKey: ['ps-ratings', 'audit'],
    queryFn: Api.psRatingsAudit,
  })

  const sortedRows = useMemo(
    () => sortAuditRows(auditQuery.data?.rows ?? [], sortKey, sortDirection),
    [auditQuery.data?.rows, sortDirection, sortKey],
  )

  const onSort = (key: AuditSortKey) => {
    if (key === sortKey) {
      setSortDirection(nextSortDirection)
      return
    }
    setSortKey(key)
    setSortDirection(
      key === 'changed_at' || key === 'old_rating' || key === 'new_rating' || key === 'delta' ? 'desc' : 'asc',
    )
  }

  const err = auditQuery.error

  return (
    <PageScrollLayout
      title="Pre-Season Ratings Audit Log"
      subtitle="Append-only history of effective PS rating changes, including zero-sum offsets applied to other teams."
    >
      {err && (
        <div className="rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900">
          <p className="font-medium">Could not load audit log.</p>
          <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words font-mono text-xs text-rose-800">
            {err instanceof ApiError ? err.message : String(err)}
          </pre>
        </div>
      )}

      {auditQuery.isLoading ? (
        <p className="text-slate-500">Loading audit log…</p>
      ) : !sortedRows.length ? (
        <div className="rounded-md border border-slate-200 bg-white p-6 text-sm text-slate-600">
          No PS rating changes have been saved yet.
        </div>
      ) : (
        <div className="table-viewport-region flex h-[min(32rem,calc(100dvh-14rem))] min-h-0 flex-col">
          <DataTableCard fillViewport caption={`${sortedRows.length} changes`}>
            <table className="table">
              <thead>
                <tr>
                  <SortableTableHeader label="Date" sortKey="changed_at" activeKey={sortKey} direction={sortDirection} onSort={onSort} />
                  <SortableTableHeader label="Team" sortKey="team" activeKey={sortKey} direction={sortDirection} onSort={onSort} />
                  <SortableTableHeader label="Old" sortKey="old_rating" activeKey={sortKey} direction={sortDirection} onSort={onSort} />
                  <SortableTableHeader label="New" sortKey="new_rating" activeKey={sortKey} direction={sortDirection} onSort={onSort} />
                  <SortableTableHeader label="Delta" sortKey="delta" activeKey={sortKey} direction={sortDirection} onSort={onSort} />
                  <SortableTableHeader label="User" sortKey="changed_by" activeKey={sortKey} direction={sortDirection} onSort={onSort} />
                  <SortableTableHeader label="Reason" sortKey="reason" activeKey={sortKey} direction={sortDirection} onSort={onSort} />
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((row) => (
                  <tr key={row.change_id}>
                    <td className="whitespace-nowrap">{formatDateTime(row.changed_at)}</td>
                    <td className="font-medium">{row.team}</td>
                    <td className="font-mono tabular-nums">{fmtRating(row.old_rating)}</td>
                    <td className="font-mono tabular-nums">{fmtRating(row.new_rating)}</td>
                    <td className="font-mono tabular-nums">{signed(row.new_rating - row.old_rating)}</td>
                    <td>{row.changed_by}</td>
                    <td>{row.reason || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </DataTableCard>
        </div>
      )}
    </PageScrollLayout>
  )
}

function sortAuditRows(rows: PsRatingAuditRow[], key: AuditSortKey, direction: SortDirection): PsRatingAuditRow[] {
  const multiplier = direction === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => {
    const primary = compareSortValue(auditSortValue(a, key), auditSortValue(b, key))
    if (primary !== 0) return primary * multiplier
    return String(b.changed_at).localeCompare(String(a.changed_at))
  })
}

function auditSortValue(row: PsRatingAuditRow, key: AuditSortKey): number | string {
  if (key === 'delta') return row.new_rating - row.old_rating
  return row[key]
}

function compareSortValue(a: number | string, b: number | string): number {
  if (typeof a === 'number' && typeof b === 'number') return a - b
  return String(a ?? '').localeCompare(String(b ?? ''))
}
